'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ArrowRight, Info, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import OperatingSystemTag from '../bots/common/OperatingSystemTag';
import PurchaseRentalDateCal from '../bots/PurchaseRentalDateCal';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/react-hooks';
import { BOT, CONFIRM_ORDER, ORDER } from '@/lib/graphql/queries';
import { EXTEND_ORDER, GET_RENTAL_MAX_DATE } from '@/lib/graphql/mutations';
import { useQuery } from '@apollo/react-hooks';
import { GET_RENTAL_PRICING } from '@/lib/graphql/mutations';
import { analytics } from '@/lib/analytics';
import { WHITE_LABEL_STRIPE_PROMISES } from '@/lib/white-label';
import { toast } from 'react-toastify';
import { Column } from '../Flex';
import styled from 'styled-components';
import { Row } from '../Flex';
import { formatCentsAsUSD } from '@/lib/currency';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { DialogContent } from '../ui/dialog';
import { Dialog } from '../ui/dialog';

const Container = styled(Row)`
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  @media only screen and (max-width: 800px) {
    flex-direction: column;
    width: 100%;
  }
`;

const StickyHeader = styled.div`
  position: sticky;
  background-color: #fff;
  z-index: 10;
  top: 0px;
  padding-top: 20px;
  padding-bottom: 10px;

  @media only screen and (max-width: 800px) {
    top: 25px;
    padding-top: 0px;
  }
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  max-width: 600px;
  width: 100%;
`;

export default function ExtendPurchase({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const [isTimeChecked, setIsTimeChecked] = useState(false);
  const [isTosChecked, setIsTosChecked] = useState(false);
  const [start, setStart] = useState(dayjs());
  const [end, setEnd] = useState(dayjs());
  const [unableToExtend, setUnableToExtend] = useState(false);
  const [maxEndDate, setMaxEndDate] = useState(null);
  const [totalCost, setTotalCost] = useState(0);
  const [rentalLength, setRentalLength] = useState(0);
  const [selectedListingPricePerDay, setSelectedListingPricePerDay] =
    useState(0);

  const { data: orderData, loading: orderLoading } = useQuery(ORDER, {
    variables: { id: Number(id) },
    skip: !id,
  });

  const { data: botData, loading: botLoading } = useQuery(BOT, {
    variables: { id: orderData?.order?.listing?.bot?.id },
    skip: !orderData?.order?.listing?.bot?.id,
  });

  const { data: confirmOrderData } = useQuery(CONFIRM_ORDER, {
    variables: { id: Number(orderData?.order?.listing?.id) },
    skip: !orderData?.order?.listing?.id,
  });

  const [getRentalPricing, { loading: rentalPriceLoading }] =
    useMutation(GET_RENTAL_PRICING);
  const [getRentalMaxDate, { loading: rentalMaxDateLoading }] =
    useMutation(GET_RENTAL_MAX_DATE);
  const [extendOrder] = useMutation(EXTEND_ORDER);

  useEffect(() => {
    if (orderData?.order) {
      const currentEndDate = dayjs(orderData.order.endDate);
      setStart(currentEndDate);
      setEnd(currentEndDate.clone().add(1, 'day'));
    }
  }, [orderData?.order?.endDate]);

  useEffect(() => {
    if (start && botData?.bot) {
      getRentalMaxDate({
        variables: {
          startData: {
            start,
            botId: botData.bot.id,
          },
        },
      }).then(({ data }) => {
        if (data?.getRentalListingMaxDate) {
          setMaxEndDate(dayjs(data.getRentalListingMaxDate.maxDate));
        }
      });
    }
  }, [start, botData]);

  useEffect(() => {
    if (start && end && botData?.bot?.id && orderData?.order?.listing?.id) {
      getRentalPricing({
        variables: {
          priceData: {
            start,
            end,
            botId: botData.bot.id,
            listingId: orderData?.order?.listing?.id,
          },
        },
      })
        .then(({ data }) => {
          if (data?.getRentalPricing) {
            setTotalCost(data.getRentalPricing.totalPrice);
            setRentalLength(end.diff(start, 'days'));
            setSelectedListingPricePerDay(data.getRentalPricing.avgPricePerDay);
            setIsTimeChecked(false);
            setIsTosChecked(false);
          }
        })
        .catch((err) => {
          if (
            err.message ===
            'No rentals found. Please select a different date range.'
          ) {
            setUnableToExtend(true);
          }
        });
    }
  }, [start, end, botData?.bot?.id]);

  const handleExtend = async () => {
    setIsLoading(true);
    try {
      const { data: extendOrderData } = await extendOrder({
        variables: {
          options: {
            id: Number(orderData?.order?.id),
            newEndDate: end.toISOString(),
          },
        },
      });

      if (extendOrderData?.extendOrder?.includes('credit-')) {
        // This is a full credit order with no requirement of stripe
        analytics.track('Credit Order Extension Accepted');
        toast.success('Order extended successfully');
        // router.push('/dashboard/transactions/purchases');
      } else {
        const stripe = await WHITE_LABEL_STRIPE_PROMISES[
          confirmOrderData.listing.whiteLabelOrg
        ];
        analytics.track('Sent to Stripe Checkout for Extension', {
          sessionId: extendOrderData.extendOrder,
        });
        await stripe.redirectToCheckout({
          sessionId: extendOrderData.extendOrder,
        });
      }
    } catch (err) {
      if (err.message === 'MUST_BE_AUTHENTICATED') {
        analytics.track('Login to Extend Order', {
          orderId: id,
          newEndDate: end.toISOString(),
        });
        // router.push('/login');
        alert('Make sure you are logged in!');
      } else if (err.message === 'MUST_AUTHENTICATE_DISCORD') {
        analytics.track('Requires Discord to Extend Order', {
          orderId: id,
          newEndDate: end.toISOString(),
        });
        // router.push('/register/connect-discord');
        alert('Connect discord to extend order');
      } else {
        analytics.track('Failed to Extend Order', {
          orderId: id,
          newEndDate: end.toISOString(),
          errorMessage: err.message,
        });
        toast.error(err.message || 'Failed to extend order. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const quickSelectOptions = [
    { label: '24 Hr', days: 1 },
    { label: '48 Hr', days: 2 },
    { label: '7 Days', days: 7 },
    { label: '30 Days', days: 30 },
  ];

  const handleQuickSelect = (days: number | null) => {
    if (days === null) return; // Custom option, handled by popover
    const newDate = dayjs(orderData?.order?.endDate).clone().add(days, 'days');
    console.log({
      currentEndDate: orderData?.order?.endDate,
      newDate,
    });
    setEnd(newDate);
  };

  if (orderLoading || botLoading || !orderData || isLoading) {
    return (
      <Container>
        <Column>
          <StickyHeader>
            <p className="lg:mb-5 text-2xl md:text-3xl pt-0 pb-2">
              <span className="font-bold">
                Extend {botData?.bot?.name} Order
              </span>
            </p>
          </StickyHeader>
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="h-40 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded mb-4"></div>
            <div className="h-40 bg-gray-200 rounded mb-4"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </Column>
      </Container>
    );
  }

  if (unableToExtend) {
    return (
      <div>
        <CardHeader>
          <h1 className="text-3xl font-bold mb-2">Unable to Extend Order</h1>
          <div className="flex items-center space-x-4">
            <img
              src={botData?.bot?.imageUrl}
              alt={botData?.bot?.name}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div>
              <CardTitle>Hayha</CardTitle>
              <div className="flex gap-2 mt-1">
                <OperatingSystemTag text="windows" iconOnly />
                <OperatingSystemTag text="mac" iconOnly />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex-1 space-y-1">
              <label className="text-sm font-medium text-muted-foreground">
                Current End Date
              </label>
              <div className="p-2 rounded-md border bg-muted/50">
                <span className="font-medium">
                  {format(
                    dayjs(orderData?.order?.endDate).toDate(),
                    'MMM d, yyyy',
                  )}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button className="w-full" size="lg" onClick={onClose}>
            Close
          </Button>
        </CardFooter>
      </div>
    );
  }

  return (
    <div>
      <CardHeader>
        <h1 className="text-3xl font-bold mb-2">Extend Order #236</h1>
        <div className="flex items-center space-x-4">
          <img
            src={botData?.bot?.imageUrl}
            alt={botData?.bot?.name}
            className="w-16 h-16 rounded-lg object-cover"
          />
          <div>
            <CardTitle>Hayha</CardTitle>
            <div className="flex gap-2 mt-1">
              {botData?.bot?.operatingSystem.split(',').map((os) => (
                <OperatingSystemTag text={os} iconOnly />
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between space-x-4">
          <div className="flex-1 space-y-1">
            <label className="text-sm font-medium text-muted-foreground">
              Current End Date
            </label>
            <div className="p-2 rounded-md border bg-muted/50">
              <span className="font-medium">
                {format(
                  dayjs(orderData?.order?.endDate).toDate(),
                  'MMM d, yyyy',
                )}
              </span>
            </div>
          </div>
          <ArrowRight className="flex-shrink-0 text-muted-foreground" />
          <div className="flex-1 space-y-1">
            <label className="text-sm font-medium text-muted-foreground">
              New End Date
            </label>
            <PurchaseRentalDateCal
              min={dayjs(orderData?.order?.endDate)}
              sel={dayjs(end)}
              max={dayjs(maxEndDate)}
              setSel={(day) => setEnd(day)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-muted-foreground">
            Extend by
          </label>
          <div className="flex flex-wrap gap-2">
            {quickSelectOptions.map((option) => (
              <Button
                key={option.label}
                variant="outline"
                onClick={() => handleQuickSelect(option.days)}
                className={
                  end &&
                  dayjs(orderData?.order?.endDate)
                    .clone()
                    .add(option.days, 'days')
                    .toDate()
                    .getTime() === end.toDate().getTime()
                    ? 'border-primary'
                    : ''
                }
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-muted p-4 space-y-3">
          <h3 className="font-semibold">Extension Summary</h3>
          <div className="grid gap-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Extension Length</span>
              <span className="font-medium">{rentalLength} days</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Price per Day</span>
              <span className="font-medium">$10.00</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t">
              <span className="font-medium">Total Cost</span>
              <span className="font-medium">{formatCentsAsUSD(totalCost)}</span>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <div className="flex-row w-full space-y-4">
          <div className="space-y-2">
            {end && (
              <>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="terms-checked"
                    checked={isTosChecked}
                    onCheckedChange={(e) => {
                      setIsTosChecked(e === 'indeterminate' ? false : e);
                    }}
                  />
                  <Label htmlFor="terms-checked" className="cursor-pointer">
                    I accept the terms and conditions
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="time-checked"
                    checked={isTimeChecked}
                    onCheckedChange={(e) => {
                      setIsTimeChecked(e === 'indeterminate' ? false : e);
                    }}
                  />
                  <Label htmlFor="time-checked" className="cursor-pointer">
                    I understand the rental will now end at{' '}
                    {format(end.toDate(), 'MMM d, yyyy')}
                  </Label>
                </div>
              </>
            )}
          </div>
          <Button
            className="w-full"
            size="lg"
            disabled={!end || isLoading || !isTimeChecked || !isTosChecked}
            onClick={handleExtend}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Extend Order'
            )}
          </Button>
        </div>
      </CardFooter>
    </div>
  );
}
export const ExtendPurchaseModal = ({
  id,
  onClose,
  open,
}: {
  id: string;
  onClose: () => void;
  open: boolean;
}) => {
  return (
    <Dialog onOpenChange={onClose} open={open}>
      <DialogContent>
        <ExtendPurchase id={id} onClose={onClose} />
      </DialogContent>
    </Dialog>
  );
};
